import type { PlanTier } from './auth';

// ─── Per-plan entitlements (single source of truth) ──────────────────────────
// API guards, guest gating, and dashboard UI all read from here. Keep this the
// ONLY place plan limits are defined.
//
//   free    — browse-only, ≤5 menus, ≤200 items, en/ar only, 1k scans/mo
//   starter — browse-only, ≤10 menus, unlimited items/languages/scans (AED 99)
//   menu+   — everything unlimited, ordering enabled
//
// A numeric limit of `null` means unlimited. `allowedLanguages: null` means any
// language is allowed.

export interface PlanLimits {
  /** Max menus a venue may create. null = unlimited. */
  maxMenus: number | null;
  /** Max menu items combined across all menus. null = unlimited. */
  maxItems: number | null;
  /** Languages the venue may enable. null = any language. */
  allowedLanguages: string[] | null;
  /** Public menu views (QR scans) allowed per calendar month. null = unlimited. */
  monthlyScans: number | null;
  /** Whether guests can place orders (vs browse-only). */
  ordering: boolean;
}

const UNLIMITED: PlanLimits = {
  maxMenus: null,
  maxItems: null,
  allowedLanguages: null,
  monthlyScans: null,
  ordering: true,
};

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free:    { maxMenus: 5, maxItems: 200, allowedLanguages: ['en', 'ar'], monthlyScans: 1000, ordering: false },
  starter: { maxMenus: 10, maxItems: null, allowedLanguages: null,        monthlyScans: null, ordering: false },
  menu:       UNLIMITED,
  operate:    UNLIMITED,
  scale:      UNLIMITED,
  hotel:      UNLIMITED,   // Scale's entitlements; in-room dining is gated separately
  command:    UNLIMITED,
  control:    UNLIMITED, // legacy alias for menu
  enterprise: UNLIMITED, // legacy alias for command
};

export function getPlanLimits(plan: PlanTier): PlanLimits {
  return PLAN_LIMITS[plan] ?? PLAN_LIMITS.free;
}

// ─── Instagram-style Stories / Highlights entitlements ───────────────────────
// Stories are available on ALL plans (a branding feature), but the number of
// highlight groups and the media per highlight scale with the plan. Enforced in
// the API (stories.service) and surfaced in the dashboard manager.

export interface StoryLimits {
  /** Max highlight groups a venue may create. */
  maxHighlights: number;
  /** Max media items (images/videos) inside a single highlight. */
  maxMediaPerHighlight: number;
}

export const STORY_LIMITS: Record<PlanTier, StoryLimits> = {
  free:       { maxHighlights: 5,  maxMediaPerHighlight: 3 },
  starter:    { maxHighlights: 7,  maxMediaPerHighlight: 4 },
  menu:       { maxHighlights: 12, maxMediaPerHighlight: 8 },
  operate:    { maxHighlights: 20, maxMediaPerHighlight: 15 },
  scale:      { maxHighlights: 30, maxMediaPerHighlight: 25 },
  hotel:      { maxHighlights: 30, maxMediaPerHighlight: 25 },  // matches Scale
  command:    { maxHighlights: 50, maxMediaPerHighlight: 40 },
  control:    { maxHighlights: 12, maxMediaPerHighlight: 8 },  // legacy alias for menu
  enterprise: { maxHighlights: 50, maxMediaPerHighlight: 40 }, // legacy alias for command
};

export function getStoryLimits(plan: PlanTier): StoryLimits {
  return STORY_LIMITS[plan] ?? STORY_LIMITS.free;
}

/** True when `count` is below the limit (and there is room for one more). */
export function isWithinLimit(count: number, limit: number | null): boolean {
  return limit === null || count < limit;
}

/** Whether the plan lets guests place orders (vs browse-only). */
export function planAllowsOrdering(plan: PlanTier): boolean {
  return getPlanLimits(plan).ordering;
}

/** Whether a venue on this plan may enable the given language code. */
export function isLanguageAllowed(plan: PlanTier, language: string): boolean {
  const allowed = getPlanLimits(plan).allowedLanguages;
  return allowed === null || allowed.includes(language);
}

/**
 * Current QR-scan metering period as 'YYYY-MM' in Asia/Dubai time (UTC+4, no DST).
 * Used as the monthly bucket key so the scan counter resets each calendar month.
 */
export function currentScanPeriod(now: Date = new Date()): string {
  const dubai = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  return dubai.toISOString().slice(0, 7);
}
